import { isApiError } from '@botpress/client'
import { getApiClient } from '~/stores'

import { z } from 'zod'
import { analyticsBoardConfigSchema } from '../../features/analytics'
import { defaultChartLayout } from '../../features/analytics/constants'
import { PathParams, PreferenceConfig, resolveKey } from './preferences'

export const WebchatCurrentRevision = 1

/*
 * Warning! This is the source of truth for preference config.
 * Do not update the preference with any breaking changes otherwise it will break the app for users with the old preferences.
 * If you need to update the preference, create a new preference and migrate the old preference to the new one.
 */
const preferenceConfig = {
  requireOnboarding: {
    schema: z.boolean().catch(() => false),
    default: false,
    error: false,
  },
  partnerStackSignupStatus: {
    schema: z
      .object({
        outcome: z.enum(['reported', 'suppressed-old-account']),
        handledAt: z.string(),
      })
      .nullable()
      .catch(() => null),
    default: null,
    error: null,
  },
  adkCliConnected: {
    schema: z.boolean().catch(() => false),
    default: false,
    error: false,
  },
  seenAnnouncementIds: {
    schema: z.array(z.string()).catch(() => []),
    default: [],
    error: [],
  },
  '$workspaceId/botHistory': {
    schema: z.array(z.string()).catch(() => []),
    default: [],
    error: [],
  },
  '$workspaceId/$botId/analyticsGridBoard': {
    schema: z.lazy(() => analyticsBoardConfigSchema),
    default: defaultChartLayout,
  },
  '$workspaceId/$botId/webchatV2Revision': {
    schema: z.number().catch(() => 0),
    default: 0,
    error: WebchatCurrentRevision,
  },
  '$tableId/columnWidths': {
    schema: z
      .record(z.number())
      .optional()
      .catch(() => ({})),
    default: {},
    error: {},
  },
  '$workspaceId/botsViewMode': {
    schema: z.enum(['grid', 'list']).catch(() => 'grid' as const),
    default: 'grid' as const,
    error: 'grid' as const,
  },
  '$workspaceId/$botId/$kbId/enableRecrawl': {
    schema: z.boolean().catch(() => false),
    default: false,
    error: false,
  },
} as const satisfies { [key: string]: PreferenceConfig }

type AccountPreferencesConfig = typeof preferenceConfig
export type AccountPreferences = {
  [K in keyof AccountPreferencesConfig]: z.infer<AccountPreferencesConfig[K]['schema']>
}

export type GetAccountPreferenceProps<T extends keyof AccountPreferences> = [PathParams<T>] extends [never]
  ? { path: T }
  : { path: T; params: PathParams<T> }

export type SetAccountPreferenceProps<T extends keyof AccountPreferences> = GetAccountPreferenceProps<T> & {
  value: AccountPreferences[T]
}

export type SetAccountPreferencePropsUnion = {
  [K in keyof AccountPreferences]: SetAccountPreferenceProps<K>
}[keyof AccountPreferences]

const client = getApiClient()

export const getAccountPreference = async <T extends keyof AccountPreferences>(
  props: GetAccountPreferenceProps<T>
): Promise<AccountPreferences[T]> => {
  const pref = await client.getAccountPreference({ key: resolveKey(props) }).catch((err) => {
    if (isApiError(err) && err.type === 'ResourceNotFound') {
      return { value: preferenceConfig[props.path].default }
    }
    throw err
  })

  return pref.value
}

export const setAccountPreference = async (props: SetAccountPreferencePropsUnion) => {
  await client.setAccountPreference({ key: 'params' in props ? resolveKey(props) : props.path, value: props.value })
}
